import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, ArrowRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";

// Actor-facing view — every continuity card that tags the caller,
// across all their projects. Read-only; tap-through opens the project
// dashboard where they can see the card in the book.
export default function MyContinuity() {
  const nav = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    api.get("/my/continuity")
      .then(({ data }) => { if (!dead) setCards(data.cards || []); })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail)
                     || "Could not load continuity");
      })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="my-continuity">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          My Continuity
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
          Your look, project by project
        </h1>
        <div className="mt-2 text-sm text-white/55 max-w-2xl">
          Everything the costume, makeup, and hair teams have logged for
          your characters. Peek before each shoot so continuity stays
          rock solid.
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-white/45 py-16 text-center">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="cs-card rounded-2xl p-10 text-center"
             data-testid="my-continuity-empty">
          <ClipboardList size={36} className="mx-auto text-white/30 mb-4" />
          <div className="font-display text-2xl uppercase tracking-tight">
            No continuity yet
          </div>
          <div className="mt-2 text-sm text-white/55 max-w-md mx-auto">
            When a production tags you in a character card, it&apos;ll
            appear here so you can walk on set already in sync with the
            look team.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="my-continuity-grid">
          {cards.map((c) => (
            <MyCard key={c.id} card={c}
                    onOpen={() => nav(`/p/${c.project_id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}


function MyCard({ card, onOpen }) {
  const latest = (card.snapshots || [])[0];
  return (
    <button onClick={onOpen}
      className="rounded-xl border border-white/10 bg-white/[0.03] hover:border-white/25 transition-colors text-left p-5"
      data-testid={`my-continuity-card-${card.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
            {card.project_type_label || card.project_type || "Project"} · {card.project_status || "active"}
          </div>
          <div className="font-display text-lg uppercase tracking-tight text-white mt-1">
            {card.project_title || "—"}
          </div>
        </div>
        <ArrowRight size={16} className="text-white/40 shrink-0 mt-1" />
      </div>

      <div className="mt-4 pt-4 border-t border-white/8">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
          Playing
        </div>
        <div className="text-sm text-white font-semibold mt-0.5">
          {card.character_name}
        </div>
      </div>

      {latest ? (
        <div className="mt-4 rounded-lg border border-white/8 bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 flex-wrap text-[10px] tracking-[0.22em] uppercase text-white/55">
            <Sparkles size={12} className="text-[color:var(--association-theme)]" />
            Latest snapshot
            {latest.scene_ref && <span>· Sc {latest.scene_ref}</span>}
            {latest.day_ref && <span>· Day {latest.day_ref}</span>}
          </div>
          {latest.notes && (
            <div className="mt-2 text-xs text-white/75 line-clamp-3 whitespace-pre-line">
              {latest.notes}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 text-[11px] text-white/40">
          No snapshots yet — the look team hasn&apos;t stamped one.
        </div>
      )}
    </button>
  );
}
